import React, { useState, useEffect } from "react";
import { Modal, Button } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";
import { RotateCcw, Package, AlertTriangle, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";

export const RestockReturnModal = ({ restockReturn, onClose, onRestock }) => {
    const [items, setItems] = useState([]);
    const [notes, setNotes] = useState("");

    useEffect(() => {
        if (restockReturn?.items) {
            setItems(restockReturn.items.map(it => ({
                ...it,
                adjustedQty: it.qty || it.quantity || 0,
                damagedQty: 0,
                goodQty: it.qty || it.quantity || 0,
                reason: ""
            })));
        }
        setNotes("");
    }, [restockReturn]);

    const updateQty = (id, field, val) => {
        setItems(prev => prev.map(it => {
            if (it._id === id) {
                const isNumeric = ['adjustedQty', 'damagedQty', 'goodQty', 'qty'].includes(field);
                const updated = { ...it, [field]: isNumeric ? (Number(val) || 0) : val };
                
                if (field === 'adjustedQty' || field === 'damagedQty') {
                    updated.goodQty = Math.max(0, updated.adjustedQty - updated.damagedQty);
                }
                return updated;
            }
            return it;
        }));
    };

    const handleConfirm = () => {
        if (items.length === 0) return toast.error("No items to restock");
        if (items.every(it => it.adjustedQty <= 0)) {
            return toast.error("Please enter at least one positive quantity");
        }

        const restockData = items.map(it => ({
            // Backend expects 'inventoryId' and 'itemName'
            inventoryId: it.inventoryId?._id || it.inventoryId || it.item?._id || it.item,
            itemName: it.itemName || it.name || it.item?.name || it.item?.itemName || "Item",
            goodQty: it.goodQty,
            damagedQty: it.damagedQty,
            reason: it.reason || notes,
            totalQty: it.adjustedQty
        }));

        onRestock(restockReturn._id, restockData);
    };

    if (!restockReturn) return null;

    const modalFooter = (
        <div className="flex w-full gap-4">
            <Button 
                variant="secondary" 
                className="flex-1 text-[11px] font-bold h-11 rounded-xl lowercase" 
                onClick={onClose}
            >
                cancel
            </Button>
            <Button 
                variant="primary"
                className="flex-1 text-[11px] font-bold h-11 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-[0.98] lowercase" 
                onClick={handleConfirm}
            >
                <RotateCcw size={16} /> submit
            </Button>
        </div>
    );

    return (
        <Modal isOpen={!!restockReturn} onClose={onClose} title="Confirm Stock Receipt" size="max-w-2xl" footer={modalFooter}>
            <div className="space-y-5">
                {/* Header Reference */}
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <p className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-orange-500 animate-pulse" />
                        Please verify the physical quantity and condition of items received.
                    </p>
                </div>

                {/* Items List */}
                <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
                    {items.map(it => (
                        <div key={it._id} className="p-5 bg-white border border-slate-100 rounded-2xl shadow-sm hover:border-slate-200 transition-all duration-300">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center shrink-0 border border-slate-100">
                                    <Package size={20} />
                                </div>
                                <div className="truncate min-w-0 flex-1">
                                    <div className="text-[11px] font-semibold text-slate-700 truncate tracking-tight uppercase">
                                        {it.itemName || it.name || it.item?.name || it.item?.itemName || "Item"}
                                    </div>
                                    <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-tighter">
                                        Return Request: <span className="text-slate-600">{it.qty} {it.unit || 'Units'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 items-end">
                                <div className="flex flex-col">
                                    <label className="text-[10px] font-extrabold text-slate-400 uppercase mb-1.5 tracking-wider">Total Received</label>
                                    <input
                                        type="number"
                                        className="w-full px-3 py-2 text-[11px] font-semibold text-slate-700 border border-slate-200 rounded-xl focus:ring-1 ring-orange-500/20 focus:border-orange-500 outline-none transition-all bg-white"
                                        value={it.adjustedQty}
                                        onChange={(e) => updateQty(it._id, 'adjustedQty', e.target.value)}
                                        placeholder="0"
                                    />
                                </div>
                                
                                <div className="flex flex-col">
                                    <label className="text-[10px] font-extrabold text-red-400 uppercase mb-1.5 flex items-center gap-1 tracking-wider">
                                        <AlertTriangle size={12} /> Damaged
                                    </label>
                                    <input
                                        type="number"
                                        className="w-full px-3 py-2 text-[11px] font-semibold text-red-600 border border-red-100 rounded-xl focus:ring-1 ring-red-500/20 focus:border-red-400 outline-none bg-red-50/10 transition-all"
                                        value={it.damagedQty}
                                        onChange={(e) => updateQty(it._id, 'damagedQty', e.target.value)}
                                        placeholder="0"
                                        max={it.adjustedQty}
                                    />
                                </div>
                                
                                <div className="flex flex-col">
                                    <label className="text-[10px] font-extrabold text-emerald-400 uppercase mb-1.5 flex items-center gap-1 tracking-wider">
                                        <CheckCircle size={12} /> Good Stock
                                    </label>
                                    <div className="w-full px-3 py-2 text-[11px] font-bold text-emerald-600 border border-emerald-100 rounded-xl bg-emerald-50/20 text-center">
                                        {it.goodQty}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="text-[10px] font-extrabold text-slate-400 uppercase mb-1.5 block tracking-tighter">Damage Reason (if any)</label>
                                <input
                                    type="text"
                                    className={`w-full px-4 py-2.5 text-[11px] font-medium border ${it.damagedQty > 0 ? 'border-red-200 bg-red-50/10' : 'border-slate-100 bg-white'} rounded-xl focus:ring-1 ring-slate-200 focus:border-slate-300 outline-none transition-all placeholder:text-slate-300`}
                                    value={it.reason}
                                    onChange={(e) => updateQty(it._id, 'reason', e.target.value)}
                                    placeholder="Specify damage reason..."
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer Notes */}
                <div className="mt-2">
                    <label className="text-[10px] font-extrabold text-slate-400 uppercase mb-2 block tracking-widest">General Notes</label>
                    <textarea
                        className="w-full px-4 py-3 text-[11px] font-medium border border-slate-100 rounded-2xl focus:ring-1 ring-slate-100 focus:border-slate-200 outline-none resize-none bg-white transition-all placeholder:text-slate-300"
                        rows="2"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Additional notes about the return condition..."
                    />
                </div>

            </div>
        </Modal>
    );
};

